@layer components {
  .page {
    container-type: inline-size;
    display: grid;
    gap: var(--space-6);
  }

  .intro {
    display: grid;
    gap: var(--space-4);
    align-items: end;
    padding-inline: var(--space-2);
    color: var(--color-on-frame);

    @container (inline-size >= 40rem) {
      grid-template-columns: minmax(0, 1fr) auto;
    }
  }

  .heading {
    display: grid;
    gap: var(--space-3);
  }

  .title {
    font-family: var(--font-display);
    font-size: var(--text-2xl);
    font-weight: 700;
    letter-spacing: -0.01em;
    line-height: 1;
    text-shadow: var(--text-shadow-frame);
  }

  .stats {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);

    & div {
      display: flex;
      align-items: baseline;
      gap: 0.4rem;
      padding: 0.3rem 0.8rem;
      border-radius: var(--radius-full);
      background: var(--color-frame-pill);
    }

    & dt {
      color: var(--color-on-frame-muted);
      font-size: var(--text-xs);
      font-weight: 560;
    }

    & dd {
      margin: 0;
      font-size: var(--text-sm);
      font-weight: 700;
      font-variant-numeric: tabular-nums;
    }
  }

  .statsSkeleton {
    inline-size: 26rem;
    max-inline-size: 100%;
    block-size: 1.875rem;
    background-color: var(--color-frame-pill);
  }

  .alerts {
    display: grid;
    gap: var(--space-2);
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .alert {
    --alert-candy: var(--sticker-archive);
    --color-focus: var(--color-accent);

    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: center;
    gap: var(--space-3);
    min-block-size: 4rem;
    padding: var(--space-3) var(--space-5) var(--space-3) var(--space-3);
    border-radius: var(--radius-lg);
    background: var(--color-surface);
    font-size: var(--text-base);
    font-weight: 600;
    box-shadow: var(--shadow-sheet);
    text-wrap: pretty;

    &[data-level="critical"] {
      --alert-candy: var(--sticker-pdf);

      box-shadow:
        inset 0 0 0 2px color-mix(in oklch, var(--color-danger), transparent 55%),
        var(--shadow-sheet);
    }
  }

  .alertMark {
    display: grid;
    place-items: center;
    inline-size: 2.75rem;
    block-size: 2.75rem;
    border-radius: var(--radius-blob);
    background: var(--candy-gloss), var(--alert-candy);
    color: var(--sticker-ink);
    box-shadow:
      inset 0 -4px 6px -2px color-mix(in oklch, var(--alert-candy), black 22%),
      inset 0 2px 3px oklch(100% 0 0 / 55%);
    rotate: -6deg;

    & svg {
      inline-size: 1.375rem;
      block-size: 1.375rem;
      stroke-width: 2.2;
    }
  }

  .cliff {
    --level-fill: var(--color-accent);
    --level-candy: var(--sticker-folder);

    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: var(--space-3) var(--space-4);
    padding: var(--space-6);

    @container (inline-size < 30rem) {
      padding: var(--space-5) var(--space-4);
    }

    &[data-level="warn"] {
      --level-fill: color-mix(in oklch, var(--sticker-html), black 28%);
      --level-candy: var(--sticker-archive);
    }

    &[data-level="critical"] {
      --level-fill: var(--color-danger);
      --level-candy: var(--sticker-pdf);
    }
  }

  .cliffMark {
    display: grid;
    place-items: center;
    inline-size: 3.25rem;
    block-size: 3.25rem;
    border-radius: var(--radius-blob);
    background: var(--candy-gloss), var(--level-candy);
    color: var(--sticker-ink);
    box-shadow:
      inset 0 -4px 6px -2px color-mix(in oklch, var(--level-candy), black 22%),
      inset 0 2px 3px oklch(100% 0 0 / 55%);
    rotate: -6deg;

    & svg {
      inline-size: 1.625rem;
      block-size: 1.625rem;
      stroke-width: 2.1;
    }
  }

  .cliffText {
    display: grid;
    gap: 0.125rem;
    min-inline-size: 0;
  }

  .cliffTitle {
    font-family: var(--font-display);
    font-size: var(--text-lg);
    font-weight: 650;
    line-height: 1.15;
  }

  .cliffCount {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.4rem;
    font-variant-numeric: tabular-nums;

    & strong {
      font-family: var(--font-display);
      font-size: clamp(1.75rem, 1.3rem + 1.6cqi, 2.25rem);
      font-weight: 700;
      line-height: 1.05;
    }
  }

  .cliffOf {
    color: var(--color-text-muted);
    font-size: var(--text-base);
    font-weight: 560;
  }

  .cliffShare {
    align-self: center;
    color: var(--level-fill);
    font-family: var(--font-display);
    font-size: var(--text-xl);
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  .cliff > .cliffBar {
    grid-column: 1 / -1;
    block-size: 1.125rem;
  }

  .cliffFoot {
    display: flex;
    flex-wrap: wrap;
    grid-column: 1 / -1;
    gap: var(--space-1) var(--space-4);
    color: var(--color-text-muted);
    font-size: var(--text-sm);
    font-variant-numeric: tabular-nums;

    & strong {
      color: var(--color-text);
      font-weight: 650;
    }
  }

  .cliffRule {
    color: var(--color-text-faint);
  }

  .panel {
    container-type: inline-size;
    display: grid;
    gap: var(--space-5);
    padding: var(--space-6);

    @container (inline-size < 30rem) {
      padding: var(--space-5) var(--space-4);
    }
  }

  .panelHead {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--space-1) var(--space-4);
  }

  .panelTitle {
    font-family: var(--font-display);
    font-size: var(--text-xl);
    font-weight: 600;
    line-height: 1.15;
    letter-spacing: -0.005em;
  }

  .panelFigure {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.4rem;
    color: var(--color-text-muted);
    font-size: var(--text-sm);
    font-variant-numeric: tabular-nums;

    & strong {
      color: var(--color-text);
      font-size: var(--text-lg);
      font-weight: 700;
    }
  }

  .chartDay {
    font-weight: 620;
  }

  .chartEgress {
    color: var(--color-text-faint);
  }

  .meters {
    display: grid;
    gap: var(--space-5);
  }

  .meter {
    display: grid;
    gap: var(--space-2);
  }

  .meterHead {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--space-1) var(--space-3);
  }

  .meterLabel {
    font-size: var(--text-base);
    font-weight: 650;
  }

  .meterFigure {
    display: flex;
    align-items: baseline;
    gap: 0.4rem;
    font-variant-numeric: tabular-nums;

    & strong {
      font-size: var(--text-lg);
      font-weight: 700;
    }
  }

  .meterOf {
    color: var(--color-text-muted);
    font-size: var(--text-sm);
    font-weight: 560;
  }

  .meterFoot {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: var(--space-1) var(--space-3);
    font-size: var(--text-sm);
    font-variant-numeric: tabular-nums;
  }

  .meterShare {
    font-weight: 620;
  }

  .meter[data-over] > .meterFoot > .meterShare {
    color: var(--color-danger);
  }

  .meterProjected {
    color: var(--color-text-faint);
  }

  .bar {
    position: relative;
    display: block;
    block-size: 0.75rem;
    border-radius: var(--radius-full);
    background: var(--color-surface-sunken);
    box-shadow: inset 0 2px 3px color-mix(in oklch, var(--color-text), transparent 88%);
    overflow: hidden;
  }

  .barUsed,
  .barProjected {
    position: absolute;
    inset-block: 0;
    inset-inline-start: 0;
    border-radius: var(--radius-full);
  }

  .barUsed {
    background: var(--level-fill, var(--color-accent));
    box-shadow:
      inset 0 -2px 0 color-mix(in oklch, var(--level-fill, var(--color-accent)), black 20%),
      inset 0 2px 2px oklch(100% 0 0 / 28%);
    transition: inline-size var(--duration-base) var(--ease-out);
  }

  .meter[data-over] .barUsed {
    --level-fill: var(--color-danger);
  }

  .barProjected {
    background: repeating-linear-gradient(
      115deg,
      color-mix(in oklch, var(--color-accent), var(--color-surface) 58%) 0 5px,
      color-mix(in oklch, var(--color-accent), var(--color-surface) 80%) 5px 10px
    );
  }

  .split {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
    margin-block-start: var(--space-1);

    & div {
      display: flex;
      align-items: baseline;
      gap: 0.4rem;
      padding: var(--space-1) 0.7rem;
      border-radius: var(--radius-full);
      background: var(--color-surface-sunken);
    }

    & dt {
      color: var(--color-text-muted);
      font-size: var(--text-xs);
      font-weight: 560;
    }

    & dd {
      margin: 0;
      font-size: var(--text-sm);
      font-weight: 650;
      font-variant-numeric: tabular-nums;
    }
  }

  .egress {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--space-2) var(--space-3);
    padding-block-start: var(--space-4);
    border-block-start: 1px solid var(--color-border);
  }

  .egressLabel {
    font-size: var(--text-base);
    font-weight: 650;
  }

  .egressValue {
    margin-inline-start: auto;
    font-size: var(--text-lg);
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  .tag {
    display: inline-flex;
    align-items: center;
    min-block-size: 1.5rem;
    padding-inline: var(--space-2);
    border-radius: var(--radius-full);
    background: var(--color-success-soft);
    color: var(--color-success);
    font-size: var(--text-xs);
    font-weight: 650;
    white-space: nowrap;
  }

  .tableFrame {
    container-type: inline-size;
    margin-inline: calc(-1 * var(--space-2));
  }

  .costTable,
  .costTable thead,
  .costTable tbody,
  .costTable tfoot,
  .costTable tr,
  .historyTable,
  .historyTable thead,
  .historyTable tbody,
  .historyTable tr {
    display: grid;
  }

  .costTable,
  .historyTable {
    inline-size: 100%;
    font-variant-numeric: tabular-nums;
  }

  .lineName {
    font-size: var(--text-base);
    font-weight: 650;
    overflow-wrap: anywhere;
  }

  .lineCell {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-2);
    min-inline-size: 0;
  }

  .cell {
    display: grid;
    gap: 0.125rem;
    min-inline-size: 0;
  }

  .cellLabel {
    color: var(--color-text-muted);
    font-size: var(--text-xs);
    font-weight: 560;
  }

  .cellValue {
    font-size: var(--text-sm);
    font-weight: 560;
    white-space: nowrap;
  }

  .per,
  .none {
    color: var(--color-text-faint);
  }

  .totalRow {
    font-weight: 700;

    & .cellValue {
      font-size: var(--text-base);
      font-weight: 700;
    }
  }

  @container (inline-size >= 56rem) {
    .costTable {
      grid-template-columns:
        minmax(7rem, 1fr) repeat(4, minmax(0, 6.75rem)) 7rem
        repeat(2, minmax(0, 6.75rem));
    }

    .historyTable {
      grid-template-columns: minmax(6rem, 1fr) repeat(4, minmax(0, 6.75rem));
    }

    .costTable thead,
    .costTable tbody,
    .costTable tfoot,
    .costTable tr,
    .historyTable thead,
    .historyTable tbody,
    .historyTable tr {
      grid-column: 1 / -1;
      grid-template-columns: subgrid;
    }

    .costTable th,
    .historyTable th {
      padding: 0 var(--space-2) var(--space-1);
      color: var(--color-text-muted);
      font-size: var(--text-xs);
      font-weight: 620;
      text-align: end;
      white-space: nowrap;

      &[data-col="line"],
      &[data-col="month"] {
        text-align: start;
      }
    }

    .costTable td,
    .historyTable td {
      align-content: center;
      min-block-size: 3rem;
      padding: var(--space-2);
      text-align: end;
    }

    .costTable td[data-col="line"],
    .historyTable td[data-col="month"] {
      text-align: start;
    }

    .cellLabel {
      display: none;
    }

    .tableRow + .tableRow,
    .tableFoot {
      border-block-start: 1.5px solid var(--color-border);
    }

    .totalRow td[data-col="mtd"] {
      grid-column: 7;
    }

    .totalRow td[data-col="cost"] {
      grid-column: 8;
    }
  }

  @container (inline-size < 56rem) {
    .tableHead {
      position: absolute;
      inline-size: 1px;
      block-size: 1px;
      overflow: hidden;
      clip-path: inset(50%);
    }

    .tableFoot {
      margin-block-start: var(--space-2);
    }

    .tableRow,
    .totalRow {
      position: relative;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: var(--space-3);
      padding: var(--space-4) var(--space-2);
    }

    .tableRow + .tableRow::before {
      content: "";
      position: absolute;
      inset-inline: var(--space-2);
      inset-block-start: 0;
      border-block-start: 1.5px solid var(--color-border);
    }

    .totalRow {
      border-radius: var(--radius-lg);
      background: var(--color-accent-soft);
    }

    .lineCell,
    .cell[data-col="rate"] {
      grid-column: 1 / -1;
    }
  }

  @container (inline-size < 17rem) {
    .tableRow,
    .totalRow {
      grid-template-columns: minmax(0, 1fr);
    }
  }

  .topPair {
    display: grid;
    align-items: start;
    gap: var(--space-4);

    @container (inline-size >= 54rem) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: var(--space-5);
    }
  }

  .topList {
    display: grid;
    margin: calc(-1 * var(--space-2)) calc(-1 * var(--space-2)) 0;
    padding: 0;
    list-style: none;
  }

  .topRow {
    position: relative;
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: var(--space-3);
    min-block-size: 3.75rem;
    padding: var(--space-2);
    border-radius: var(--radius-lg);
    transition: background-color var(--duration-fast) var(--ease-out);

    & + &::before {
      content: "";
      position: absolute;
      inset-inline: var(--space-2);
      inset-block-start: 0;
      border-block-start: 1px solid var(--color-border);
    }

    @media (hover: hover) {
      &:hover {
        background: var(--color-surface-hover);

        &::before,
        & + &::before {
          border-color: transparent;
        }

        & > :first-child {
          rotate: 0deg;
          scale: 1.08;
        }
      }
    }

    &:has(.topName:focus-visible) {
      outline: 2.5px solid var(--color-accent);
      outline-offset: -2px;
    }
  }

  .topText {
    display: grid;
    gap: 0.0625rem;
    min-inline-size: 0;
  }

  .topName {
    overflow: hidden;
    color: var(--color-text);
    font-size: var(--text-base);
    font-weight: 620;
    text-decoration: none;
    text-overflow: ellipsis;
    white-space: nowrap;

    &::after {
      content: "";
      position: absolute;
      inset: 0;
      border-radius: inherit;
    }

    &:focus-visible {
      outline: none;
    }
  }

  .topPath {
    overflow: hidden;
    color: var(--color-text-muted);
    font-size: var(--text-xs);
    font-weight: 540;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .topFigure {
    display: grid;
    justify-items: end;
    gap: 0.0625rem;
    font-variant-numeric: tabular-nums;
  }

  .topBytes {
    font-size: var(--text-sm);
    font-weight: 700;
  }

  .topViews {
    color: var(--color-text-muted);
    font-size: var(--text-xs);
    font-weight: 540;
  }

  .listEmpty {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    min-block-size: 3.75rem;
    color: var(--color-text-muted);
    font-size: var(--text-sm);
  }

  .emptyMark {
    display: grid;
    flex: none;
    place-items: center;
    inline-size: 2.5rem;
    block-size: 2.5rem;
    border: 2px dashed var(--color-border-strong);
    border-radius: 50%;

    & svg {
      inline-size: 1.125rem;
      block-size: 1.125rem;
    }
  }

  .chart {
    --chart-height: clamp(6rem, 16vh, 9rem);

    position: relative;
  }

  .limitLine {
    position: absolute;
    inset-inline: 0;
    border-block-start: 2px dashed color-mix(in oklch, var(--color-danger), transparent 35%);
    pointer-events: none;
  }

  .limitLabel {
    position: absolute;
    inset-block-end: 0.15rem;
    inset-inline-end: 0;
    color: var(--color-danger);
    font-size: var(--text-xs);
    font-weight: 620;
    font-variant-numeric: tabular-nums;
  }

  .bars {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: minmax(0, 1fr);
    align-items: end;
    gap: 0.125rem;
    margin: 0;
    padding: 0;
    list-style: none;
    touch-action: pan-y;
  }

  .barSlot {
    display: grid;
    gap: var(--space-1);
    min-inline-size: 0;
  }

  .dayBar {
    display: grid;
    align-items: end;
    block-size: var(--chart-height);
    padding: 0;
    border: 0;
    border-radius: 0.4rem 0.4rem 0.25rem 0.25rem;
    background: none;
    transition: background-color var(--duration-fast) var(--ease-out);
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;

    @media (hover: hover) {
      &:hover {
        background: var(--color-surface-hover);
      }
    }

    &[data-picked] {
      background: var(--color-surface-hover);
    }

    &:focus-visible {
      outline: 2.5px solid var(--color-accent);
      outline-offset: 1px;
    }
  }

  .dayFill {
    display: block;
    inline-size: 100%;
    border-radius: inherit;
    background: var(--color-accent);
    box-shadow: inset 0 -2px 0 color-mix(in oklch, var(--color-accent), black 20%);
  }

  .dayLabel {
    color: var(--color-text-muted);
    font-size: var(--text-xs);
    font-weight: 560;
    font-variant-numeric: tabular-nums;
    text-align: center;
  }

  .barSlot:not(:nth-child(5n + 1)) > .dayLabel {
    visibility: hidden;
  }

  .dayBar[data-today] + .dayLabel {
    visibility: visible;
    color: var(--color-text);
    font-weight: 700;
  }

  .metaSkeleton {
    inline-size: 7rem;
    block-size: 0.875rem;
    background-color: color-mix(in oklch, var(--color-on-frame), transparent 70%);
  }

  .factsSkeleton {
    inline-size: min(22rem, 100%);
    block-size: 1.75rem;
  }

  .pendingCliff {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: center;
    gap: var(--space-3) var(--space-4);
    padding: var(--space-6);
  }

  .pendingMark {
    inline-size: 3.25rem;
    block-size: 3.25rem;
    border-radius: var(--radius-blob);
  }

  .pendingTitle {
    inline-size: min(14rem, 60%);
    block-size: 1.5rem;
  }

  .pendingBar {
    grid-column: 1 / -1;
    block-size: 0.75rem;
  }

  .pendingMeter {
    display: grid;
    gap: var(--space-2);
  }

  .pendingLine {
    inline-size: min(18rem, 70%);
    block-size: 1rem;
  }
}
